import React, { useEffect, useState, memo, useRef } from 'react';
import styles from './CheckoutButton.module.scss';
import { useSharedCart } from '../../entrypoints/theme';
import Guarantees from '../Guarantees/Guarantees';
import { cartStore } from '../../hooks/cartStore';

interface CheckoutButtonProps {
  buttonType?: 'button' | 'submit';
}

interface GuaranteeData {
  title: string;
  guarantees: Array<{
    id: number;
    iconName: string;
    title: string;
    description: string;
    image: string;
  }>;
}

const defaultGuarantees = {
  title: "Our Guarantees",
  emptyImage: "/empty_picture.png",
  guarantees: [
    {
      id: 1,
      iconName: "Satisfaction",
      title: "Satisfaction Guarantee",
      description:
        "If you're not satisfied, we'll work with you to make things right.",
      image:
        "https://cdn.shopify.com/s/files/1/0706/3348/8617/files/Satisfaction_Guarantee_7cd691d2-230c-4b8d-a1e8-dbb766ddab73.svg?v=1724449251",
    },
    {
      id: 2,
      iconName: "FastService",
      title: "Fast Service Guarantee",
      description:
        "We'll reply to your email within 24 business hours or you get a $10 gift card for your next order.",
      image:
        "https://www.solawoodflowers.com/cdn/shop/files/Fast_service_guarantee_0481b80d-0170-40d9-b170-eebebbd128dd.svg?v=1724449282&width=35",
    },
    {
      id: 3,
      iconName: "DIY",
      title: "DIY Support",
      description: "Get expert guidance and tips for your DIY flower projects.",
      image:
        "https://www.solawoodflowers.com/cdn/shop/files/DIY_Guarantee_19fa489e-a1eb-4b9c-a8ca-fc758aa9802f.svg?v=1724450047&width=35",
    },
    {
      id: 4,
      iconName: "Everlasting",
      title: "Everlasting Beauty",
      description:
        "Our wood flowers maintain their beauty without wilting or fading.",
      image:
        "https://www.solawoodflowers.com/cdn/shop/files/Everlasting_guarantee_bb945183-a18f-414a-a7c2-8be87a802900.svg?v=1724449266&width=35",
    },
    {
      id: 5,
      iconName: "OnTime",
      title: "On-Time Delivery",
      description:
        "Your order will arrive on time or we'll refund your shipping costs.",
      image:
        "https://www.solawoodflowers.com/cdn/shop/files/On_time_or_it_s_free_-_guaranteed.svg?v=1724450075&width=35",
    },
  ],
};

const CheckoutButton = memo(({ buttonType = "button" }: CheckoutButtonProps) => {
  const { cartState } = useSharedCart();
  const [cartPrice, setCartPrice] = useState<number>(0);
  const [checkboxState, setCheckboxState] = useState<boolean>(false);
  const [guaranteeData, setGuaranteeData] = useState<GuaranteeData>(defaultGuarantees);
  const nvdMiniRef = useRef<HTMLDivElement>(null);
  const [nvdPrice, setNvdPrice] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxState(e.target.checked);
    cartStore.updateNvdCheckboxState(e.target.checked);
    const shippingProtectionPriceElement = document.getElementById("shipping-protection-price") as HTMLElement | null;
    let priceValue = 0;
    if (shippingProtectionPriceElement?.innerText) {
      const priceText = shippingProtectionPriceElement.innerText.replace("$", "");
      priceValue = parseFloat(priceText);
      if (isNaN(priceValue)) {
        priceValue = 0;
      }
    }
    cartStore.updateNvdCheckboxStateValue(priceValue);
  }
  
  const getNavdiumPrice = () => {
    const checkbox = document.getElementById(
      "shippingProtectionCheckBox"
    ) as HTMLInputElement | null;
    
    if (checkbox) {
      const onclickAttr = checkbox.getAttribute('onclick');
      if (onclickAttr) {
        const match = onclickAttr.match(/getShippingProtection\([^,]+,\s*'([^']+)'/);
        if (match && match[1]) {
          const priceFromOnclick = parseFloat(match[1]);
          if (!isNaN(priceFromOnclick)) {
            return priceFromOnclick;
          }
        }
      }
    }

    return 0;
  }

  useEffect(() => {
    const checkAndLogElement = () => {
      const nvdToggleElement = document.querySelector(".nvd-toggle") as HTMLElement | null;
      const nvdWidgetWrapper = document.querySelector(".nvd-widget-wrapper") as HTMLElement | null;
      const nvdContainer = document.getElementById("nvdMini") as HTMLElement | null;

      if (nvdToggleElement && nvdContainer) {
        if (nvdWidgetWrapper) {
          nvdWidgetWrapper.style.display = "none";
        }

        setIsLoading(true);
        nvd_init().then(() => {
          const checkbox = document.getElementById(
            "shippingProtectionCheckBox"
          ) as HTMLInputElement | null;
          if (checkbox) {
            checkbox.addEventListener("change", handleCheckboxChange);
            setCheckboxState(checkbox.checked);
            const priceValue = getNavdiumPrice();
            setNvdPrice(priceValue || 0);
            cartStore.updateNvdCheckboxState(checkbox.checked);
            cartStore.updateNvdCheckboxStateValue(priceValue || 0);
          }
        }).finally(() => {
          setIsLoading(false);
        });
      }
    };

    const observer = new MutationObserver(() => {
      checkAndLogElement();
    });

    if (nvdMiniRef.current) {
      observer.observe(nvdMiniRef.current, {
        childList: true,
        subtree: true 
      });
    }
    
    if (cartState.nvd_opted_out === 0) {
      setCheckboxState(true);
    } else {
      setCheckboxState(false);
    }

    checkAndLogElement();

    return () => {
      observer.disconnect();
      const checkbox = document.getElementById("shippingProtectionCheckBox") as HTMLInputElement | null;
      if (checkbox) {
        checkbox.removeEventListener("change", handleCheckboxChange);
      }
    };
  }, []);

  useEffect(() => {
    if (checkboxState && nvdPrice > 0) {
      const cartTotal = parseFloat(cartState.main_items_total) + (nvdPrice || 0);
      setCartPrice(cartTotal.toFixed(2));
    } else {
      setCartPrice(cartState.main_items_total);
    }
  }, [checkboxState, cartState, nvdPrice]);

  useEffect(() => {
    const guaranteesElement = document.getElementById('guarantees-data');
    if (guaranteesElement?.dataset) {
      const data = guaranteesElement.dataset;
      setGuaranteeData({
        title: data.title || defaultGuarantees.title,
        emptyImage: data.emptyImage || defaultGuarantees.emptyImage,
        guarantees: [
          {
            id: 1,
            iconName: "Satisfaction",
            title:
              data.guarantee1Title || defaultGuarantees.guarantees[0].title,
            description:
              data.guarantee1Content ||
              defaultGuarantees.guarantees[0].description,
            image:
              "https://cdn.shopify.com/s/files/1/0706/3348/8617/files/Satisfaction_Guarantee_7cd691d2-230c-4b8d-a1e8-dbb766ddab73.svg?v=1724449251",
          },
          {
            id: 2,
            iconName: "FastService",
            title:
              data.guarantee2Title || defaultGuarantees.guarantees[1].title,
            description:
              data.guarantee2Content ||
              defaultGuarantees.guarantees[1].description,
            image:
              "https://www.solawoodflowers.com/cdn/shop/files/Fast_service_guarantee_0481b80d-0170-40d9-b170-eebebbd128dd.svg?v=1724449282&width=35",
          },
          {
            id: 3,
            iconName: "DIY",
            title:
              data.guarantee3Title || defaultGuarantees.guarantees[2].title,
            description:
              data.guarantee3Content ||
              defaultGuarantees.guarantees[2].description,
            image:
              "https://www.solawoodflowers.com/cdn/shop/files/DIY_Guarantee_19fa489e-a1eb-4b9c-a8ca-fc758aa9802f.svg?v=1724450047&width=35",
          },
          {
            id: 4,
            iconName: "Everlasting",
            title:
              data.guarantee4Title || defaultGuarantees.guarantees[3].title,
            description:
              data.guarantee4Content ||
              defaultGuarantees.guarantees[3].description,
            image:
              "https://www.solawoodflowers.com/cdn/shop/files/Everlasting_guarantee_bb945183-a18f-414a-a7c2-8be87a802900.svg?v=1724449266&width=35",
          },
          {
            id: 5,
            iconName: "OnTime",
            title:
              data.guarantee5Title || defaultGuarantees.guarantees[4].title,
            description:
              data.guarantee5Content ||
              defaultGuarantees.guarantees[4].description,
            image:
              "https://www.solawoodflowers.com/cdn/shop/files/On_time_or_it_s_free_-_guaranteed.svg?v=1724450075&width=35",
          },
        ],
      });
    }
  }, []);

  return (
    <>
      {buttonType === "button" ? (
        <>
          <a
            href="/cart"
            className={`${styles.button}`}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              if (cartState?.count <= 0) {
                e.preventDefault();
              }
            }}
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.5 : 1,
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
          >
            VIEW CART & CHECKOUT <span className={styles.separator}>•</span>{" "}
            <span className={styles.checkoutButtonPrice}>
              $
              {Number(cartPrice).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </a>
          <div style={{ height: "60px", margin: "-12px auto 22px auto" }}>
            <div id="nvdMini" className="nvd-mini" ref={nvdMiniRef}></div>
          </div>
        </>
      ) : (
        <>
          <button
            type="submit"
            name="checkout"
            className={`${styles.checkoutButton}`}
            disabled={cartState?.count <= 0 || isLoading}
            style={{
              opacity: isLoading ? 0.5 : 1,
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
          >
            CHECKOUT <span className={styles.separator}>•</span>{" "}
            <span className={styles.checkoutButtonPrice}>
              $
              {Number(cartPrice).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </button>
          <div id="nvdContainer" className={styles.nvdContainer}>
            <div id="nvdMini" className="nvd-mini" ref={nvdMiniRef}></div>
          </div>
          <Guarantees
            title={guaranteeData.title}
            emptyImage={guaranteeData.emptyImage}
            guarantees={guaranteeData.guarantees}
          />
        </>
      )}
    </>
  );
});

export default CheckoutButton;

CheckoutButton.displayName = "CheckoutButton";
